import { useAtom } from 'jotai';
import { useEffect, useMemo } from 'react';

import useQueryBuilderParams from '@/components/QueryBuilder/QueryBuilder.hooks';
import type {
  QBFilter,
  QBFilterOptions
} from '@/components/QueryBuilder/QueryBuilder.types';
import QueryBuilderBar from '@/components/QueryBuilder/QueryBuilderBar';
import { VULNERABILITIES_FILTERS_STORAGE_KEY } from '@/constants/common';
import { mergeSearchableSelectFieldOptions } from '@/features/SearchBar';
import usePermissionsCheck from '@/hooks/usePermissionsCheck';

import { selectedOrganizationAtom } from '../Organizations';
import { mapFiltersToRequestParams } from '../Tickets/Tickets.store';
import { normalizeCustomerTicketAuthorFilters } from '../Tickets/Tickets.utils';
import { VULNERABILITIES_GROUP_BY_FIELDS } from './VMPVulnerabilities/VMPVulnerabilities.constants';
import {
  vulnerabilitiesFiltersAtom,
  vulnerabilitiesTimeFilterAtom
} from './VMPVulnerabilities/VMPVulnerabilities.store';
import { VMP_SEARCHABLE_SELECT_FILTERS_VULNERABILITIES } from './VulnerabilityManagement.constants';
import {
  useFetchVulnerabilitiesGroupByOptions,
  useFetchVulnerabilityFilters,
  vulnerabilitiesGroupsAtom
} from './VulnerabilityManagement.store';
import type { VulnerabilityManagementSearchProps } from './VulnerabilityManagement.types';

const VulnerabilityManagementSearch = ({
  onFiltersReady
}: VulnerabilityManagementSearchProps) => {
  const userIsInternal = usePermissionsCheck('internal');
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const [vulnerabilityFilters, setVulnerabilitiesFilters] = useAtom(
    vulnerabilitiesFiltersAtom
  );
  const [vulnerabilityTimeFilter, setVulnerabilitiesTimeFilter] = useAtom(
    vulnerabilitiesTimeFilterAtom
  );
  const [, setVulnerabilitiesGroups] = useAtom(vulnerabilitiesGroupsAtom);

  const {
    data: vulnerabilityFiltersConfig,
    isLoading: isLoadingVulnerabilityFilters
  } = useFetchVulnerabilityFilters();

  const requestParams = useMemo(
    () =>
      mapFiltersToRequestParams(
        vulnerabilityFilters || [],
        vulnerabilityTimeFilter,
        'date_identified',
        'last_changed_at'
      ),
    [vulnerabilityFilters, vulnerabilityTimeFilter]
  );

  const normalizeFilters = (filters: QBFilter[]) =>
    normalizeCustomerTicketAuthorFilters(filters, userIsInternal);

  const searchParams = useMemo(() => {
    if (selectedOrganization && selectedOrganization.id > 0) {
      return {
        ...requestParams,
        organization__label: selectedOrganization.label
      };
    }
    return requestParams;
  }, [requestParams, selectedOrganization]);

  const { isReady } = useQueryBuilderParams({
    config: vulnerabilityFiltersConfig || {},
    isLoading: isLoadingVulnerabilityFilters,
    sessionKey: `${VULNERABILITIES_FILTERS_STORAGE_KEY}V2`,
    normalizeFilters,
    setFilters: setVulnerabilitiesFilters,
    setTimeFilter: setVulnerabilitiesTimeFilter
  });

  useEffect(() => {
    onFiltersReady(isReady);
  }, [isReady, onFiltersReady]);

  const { data: groupByOptions } = useFetchVulnerabilitiesGroupByOptions(
    VULNERABILITIES_GROUP_BY_FIELDS,
    searchParams,
    setVulnerabilitiesGroups,
    isReady
  );

  const vulnerabilitiesSearchableFilters = useMemo(() => {
    if (!vulnerabilityFiltersConfig || !groupByOptions) return [];

    const searchableSelectFilters =
      VMP_SEARCHABLE_SELECT_FILTERS_VULNERABILITIES.map((f) => {
        const filter = { ...f };
        const { field } = filter;
        let { filterOptions } = filter;

        // group-by results for null-checked fields (e.g. "date_remediated") are keyed
        // by the "__isnull" group-by field name, not the base filter field name
        const groupByKey = groupByOptions[field] ? field : `${field}__isnull`;

        if (groupByOptions[groupByKey]) {
          filterOptions = mergeSearchableSelectFieldOptions(
            filter.filterOptions,
            groupByOptions[groupByKey] as QBFilterOptions[]
          );
        }

        // if options are defined in the constants, ensure the sort order is maintained
        if (filter.filterOptions.length > 0) {
          const sortingValues = filter.filterOptions.map(
            (option) => option.value
          );

          filterOptions = filterOptions.sort((a, b) =>
            sortingValues.indexOf(a.value) > sortingValues.indexOf(b.value)
              ? 1
              : -1
          );
        }

        return {
          ...filter,
          filterOptions
        };
      });

    return searchableSelectFilters;
  }, [userIsInternal, groupByOptions, vulnerabilityFiltersConfig]);

  if (!vulnerabilityFiltersConfig) {
    return null;
  }

  return (
    <QueryBuilderBar
      enableTimeFilter
      config={vulnerabilityFiltersConfig}
      options={groupByOptions}
      sessionKey={`${VULNERABILITIES_FILTERS_STORAGE_KEY}V2`}
      searchableSelectFilters={vulnerabilitiesSearchableFilters}
      filters={vulnerabilityFilters || []}
      timeFilter={vulnerabilityTimeFilter}
    />
  );
};

export default VulnerabilityManagementSearch;
